/**
 * Actions sur le terrain lui-meme, sans noeud de ressource.
 *
 * Certaines matieres n'ont pas de "chose" a recolter: on creuse le sable de la
 * plage, on peche au bord de l'eau. Sans ca, `sable` et `poisson` seraient
 * introuvables, et donc la fusee (5 sable), la brochette (2 poisson), la
 * piscine et la cabane de plage seraient impossibles a obtenir.
 *
 * Module pur: aucune dependance au DOM ni a three.js.
 */
import { REACH } from '../constants.ts'
import type { Cout, Inventaire } from '../types/items.ts'
import type { ITerrain, Vec2 } from '../types/world.ts'
import type { Alea } from './alea.ts'

export type ActionTerrain = 'creuser' | 'pecher'

/** Duree d'une action, en secondes. Pecher demande de la patience. */
export const DUREE_ACTION: Record<ActionTerrain, number> = {
  creuser: 1.1,
  pecher: 2.6,
}

export const NOM_ACTION: Record<ActionTerrain, string> = {
  creuser: 'Creuser le sable',
  pecher: 'Pêcher',
}

/** Nombre de points d'echantillonnage autour du joueur pour trouver l'eau. */
const RAYONS_SONDE = 8

/**
 * Portee de lancer avec une canne a peche, en metres.
 *
 * POURQUOI CE NOMBRE. Sans canne on peche a bout de bras (REACH = 2.2 m), et
 * c'est precisement ce qui rendait la peche introuvable: il fallait deja se
 * tenir au ras de l'eau pour que quoi que ce soit l'annonce. Le journal d'une
 * session entiere le montre — trente-sept fois « Creuser le sable », une seule
 * fois un mot de peche. Neuf metres, c'est quatre fois le bras et la demi-hauteur
 * du cadrage par defaut (CAM_ZOOM_DEFAULT): l'eau visee tient a l'ecran, donc on
 * peut viser ce qu'on voit.
 *
 * On ne va pas plus loin volontairement: au-dela, un tap sur l'eau proche ne
 * pourrait plus vouloir dire « va nager », et on aurait echange une decouverte
 * contre une autre.
 */
export const PORTEE_CANNE = 9

/** Vrai si le joueur a une canne a peche dans son sac. */
export function aCanne(inv: Inventaire): boolean {
  return (inv.canne ?? 0) > 0
}

/**
 * Jusqu'ou l'on peut lancer une ligne. Une seule fonction pour la question, pour
 * que l'indice affiche et le geste accepte ne puissent pas diverger.
 */
export function porteeLancer(inv: Inventaire): number {
  return aCanne(inv) ? PORTEE_CANNE : REACH
}

/** On ne peut creuser que dans du sable, hors de l'eau. */
export function peutCreuser(terrain: ITerrain, x: number, z: number): boolean {
  if (terrain.estEau(x, z)) return false
  return terrain.echantillon(x, z).sol === 'sable'
}

/**
 * On peche si de l'eau se trouve a portee: on sonde un anneau autour du point
 * plutot que le point lui-meme, pour pouvoir pecher depuis la berge.
 */
export function eauAPortee(terrain: ITerrain, x: number, z: number, portee = REACH): Vec2 | null {
  if (terrain.estEau(x, z)) return { x, z }
  for (let i = 0; i < RAYONS_SONDE; i++) {
    const a = (i / RAYONS_SONDE) * Math.PI * 2
    const px = x + Math.cos(a) * portee
    const pz = z + Math.sin(a) * portee
    if (terrain.estEau(px, pz)) return { x: px, z: pz }
  }
  return null
}

export function peutPecher(terrain: ITerrain, x: number, z: number, portee = REACH): boolean {
  return eauAPortee(terrain, x, z, portee) !== null && !terrain.estEau(x, z)
}

/** Ce que rapporte une action. La peche echoue parfois: c'est la peche. */
export function produitAction(action: ActionTerrain, alea: Alea): Cout {
  if (action === 'creuser') {
    // Un coup de pelle rapporte du sable, et parfois de la fibre (algues seches).
    const sable = alea.entier(2, 3)
    return alea.chance(0.15) ? { sable, fibre: 1 } : { sable }
  }
  // Pecher: deux fois sur trois un poisson, sinon rien (mais l'energie est
  // depensee: on reessaie).
  return alea.chance(0.66) ? { poisson: 1 } : {}
}

/**
 * L'action possible a cet endroit, ou null.
 *
 * Le sable est prioritaire: sur la bande de plage, l'eau est presque toujours a
 * portee, si bien que donner la priorite a la peche rendait le creusage
 * inaccessible — et le sable est la seule source pour les fusees.
 */
export function actionDisponible(terrain: ITerrain, x: number, z: number): ActionTerrain | null {
  if (peutCreuser(terrain, x, z)) return 'creuser'
  if (peutPecher(terrain, x, z)) return 'pecher'
  return null
}

/**
 * L'action de terrain VISEE par un tap en (x, z), le joueur se tenant en
 * `depuis`.
 *
 * POURQUOI CETTE FONCTION EXISTE, alors que `actionDisponible` semblait suffire.
 * Cette derniere arbitre par ordre de priorite, et sur une plage l'arbitrage est
 * toujours perdu par la peche: le sol est du sable, donc `creuser` gagne, partout,
 * tout le temps. Pecher depuis la rive etait donc litteralement inatteignable —
 * du code present, teste, et qu'aucun geste ne pouvait declencher.
 *
 * Il n'y a en realite rien a arbitrer: ce que le joueur veut est dit par
 * l'endroit qu'il touche. On touche l'eau, on peche; on touche le sable, on
 * creuse. Aucune priorite, aucune surprise.
 *
 * Toucher le sable plus loin que le bras est un ordre de deplacement, aussi.
 *
 * Toucher l'eau PLUS LOIN que la portee de lancer reste un ordre de deplacement:
 * c'est ainsi qu'on part a la nage, et c'est ce qui distingue les deux
 * intentions. `portee` vaut donc le bras sans canne, et neuf metres avec.
 */
export function actionVisee(
  terrain: ITerrain,
  x: number,
  z: number,
  depuis: Vec2,
  portee = REACH,
): ActionTerrain | null {
  if (terrain.estEau(x, z)) {
    // On ne peche pas en nageant: il faut avoir les pieds sur la terre ferme.
    if (terrain.estEau(depuis.x, depuis.z)) return null
    const dx = x - depuis.x
    const dz = z - depuis.z
    return dx * dx + dz * dz <= portee * portee ? 'pecher' : null
  }
  /*
   * LE SABLE SE CREUSE A BOUT DE BRAS, pas au bout du monde. Un tap sur la
   * plage lointaine faisait marcher puis creuser a l'arrivee: sur une plage, on
   * ne pouvait plus simplement aller quelque part (vu au telephone le 13
   * octobre; choix de JC). Comme l'eau: au-dela du bras, c'est un deplacement.
   */
  if (peutCreuser(terrain, x, z)) {
    const dx = x - depuis.x
    const dz = z - depuis.z
    return dx * dx + dz * dz <= REACH * REACH ? 'creuser' : null
  }
  return null
}
